import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "../api";
import { AUTH, TRIAL } from "../constants/testIds";
import { fadeIn } from "../lib/motion";
import { useCelebration } from "../contexts/CelebrationContext";
import { TrialSignupForm } from "../components/trial/TrialSignupForm";
import { KairooAttribution } from "../components/common/KairooAttribution";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateField(name, value) {
  const v = (value || "").trim();
  if (name === "email") {
    if (!v) return "Email is required";
    if (!EMAIL_RE.test(v)) return "Enter a valid email address";
  }
  if (name === "password") {
    if (!v) return "Password is required";
    if (v.length < 6) return "Password must be at least 6 characters";
  }
  if (name === "mfaCode") {
    if (!v) return "Enter the code we sent you";
    if (!/^\d{4,8}$/.test(v)) return "Code must be 4-8 digits";
  }
  if (name === "resetEmail") {
    if (!v) return "Email is required";
    if (!EMAIL_RE.test(v)) return "Enter a valid email address";
  }
  if (name === "resetToken") {
    if (!v) return "Enter the reset token from your email";
  }
  if (name === "resetNewPassword") {
    if (!v) return "New password is required";
    if (v.length < 12) return "Password must be at least 12 characters";
  }
  return "";
}

// Premium, theme-independent surface: a full-bleed looping video hero (Mixkit, free-to-use)
// with a 30% black overlay for legibility, the auth card anchored to the right half. Kept
// deliberately outside the app's day/night ThemeContext — a branded, dark-glass card reads
// correctly against ANY video frame, which a toggle-able light/dark card could not guarantee.
export default function Login() {
  const { celebrate } = useCelebration();
  const [mode, setMode] = useState("login"); // "login" | "trial"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaPending, setMfaPending] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [resetRequested, setResetRequested] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [resetNewPassword, setResetNewPassword] = useState("");
  const [resetConfirmPassword, setResetConfirmPassword] = useState("");
  const [resetDone, setResetDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [videoSrc, setVideoSrc] = useState(null);
  const navigate = useNavigate();
  const reducedMotion = useReducedMotion();

  // testing-agent finding (iteration_21): this dev environment's proxy sends
  // `Cache-Control: no-store` on every response, including static /videos/* files. Chromium's
  // native <video> element relies on the browser's internal media byte-range cache to
  // coordinate buffering — `no-store` defeats that specifically for <video>, causing repeated
  // net::ERR_ABORTED on its range requests even though a plain fetch()/XHR against the exact
  // same URL succeeds fine. Fetching it as a blob once and handing the video element a local
  // blob: URL sidesteps the browser's HTTP media pipeline entirely after the initial fetch.
  useEffect(() => {
    let blobUrl;
    let cancelled = false;
    fetch("/videos/login-bg-720.mp4")
      .then((res) => res.blob())
      .then((blob) => {
        if (cancelled) return;
        blobUrl = URL.createObjectURL(blob);
        setVideoSrc(blobUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, []);

  const runValidation = (fields) => {
    const errs = {};
    for (const [name, value] of fields) {
      const msg = validateField(name, value);
      if (msg) errs[name] = msg;
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const routeAfterAuth = async () => {
    const { data: me } = await api.get("/me");
    if (me.user_type === "kairoo") {
      await api.post("/auth/logout");
      setMfaPending(false);
      setError("This is the gym portal. Kairoo platform operators should sign in at the Control Plane portal instead.");
      return;
    }
    // UX-FD-01/03: Front Desk AND Manager-tier staff share the same slim Desk shell (Manager
    // gets an extra nav section inside it) — neither ever renders the org-wide Owner Cockpit.
    // UX-TR-01: trainer is its own persona surface too.
    if (["front_of_house_fd", "front_of_house_mgr"].includes(me.role)) { navigate("/desk/today"); return; }
    if (me.role === "trainer") { navigate("/trainer/today"); return; }
    // UX-P04: Implementation Team's whole job is a one-time go-live, not running the gym day to
    // day — land straight on Migration Studio rather than an Owner Cockpit dashboard most of
    // whose tiles it has no permission to see.
    if (me.role === "implementation_team") { navigate("/app/migration"); return; }
    navigate("/app/dashboard");
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    setError("");
    if (!runValidation([["email", email], ["password", password]])) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_required) setMfaPending(true);
      else if (data.must_change_password) setMustChangePassword(true);
      else await routeAfterAuth();
    } catch (e2) {
      setError(formatApiError(e2).message);
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError("");
    if (!runValidation([["mfaCode", mfaCode]])) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/verify", { code: mfaCode });
      if (data.must_change_password) { setMfaPending(false); setMustChangePassword(true); }
      else await routeAfterAuth();
    } catch (e2) {
      setError(formatApiError(e2).message);
    } finally {
      setBusy(false);
    }
  };

  // DEFECT-D-04: the ONLY route reachable with a `must_change_password` session (every other API
  // call 403s with MUST_CHANGE_PASSWORD, per auth_deps.get_context's own gate) — sets a real
  // password, then proceeds exactly like a normal login.
  const submitNewPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (newPassword !== confirmPassword) { setError("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/set-new-password", { new_password: newPassword });
      celebrate("password_changed");
      await routeAfterAuth();
    } catch (e2) {
      setError(formatApiError(e2).message);
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setResetMessage("");
    if (!runValidation([["resetEmail", resetEmail]])) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/password-reset/request", { email: resetEmail });
      setResetMessage(data.message);
      setResetRequested(true);
    } catch (e2) {
      setResetMessage(formatApiError(e2).message);
    } finally {
      setBusy(false);
    }
  };

  const submitResetConfirm = async (e) => {
    e.preventDefault();
    setResetMessage("");
    if (!runValidation([["resetToken", resetToken], ["resetNewPassword", resetNewPassword]])) return;
    if (resetNewPassword !== resetConfirmPassword) {
      setFieldErrors((prev) => ({ ...prev, resetConfirmPassword: "Passwords do not match" }));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/password-reset/confirm", { token: resetToken.trim(), new_password: resetNewPassword });
      setResetDone(true);
      celebrate("password_changed");
    } catch (e2) {
      setResetMessage(formatApiError(e2).message);
    } finally {
      setBusy(false);
    }
  };

  const backToLogin = () => {
    setShowReset(false);
    setResetRequested(false);
    setResetDone(false);
    setResetEmail("");
    setResetToken("");
    setResetNewPassword("");
    setResetConfirmPassword("");
    setResetMessage("");
    setFieldErrors({});
  };

  return (
    <div data-testid="login-page" className="relative min-h-screen w-full overflow-hidden flex items-center justify-center md:justify-end" style={{ background: "#0a0a0c" }}>
      {/* Self-hosted (Mixkit's CDN forces Content-Disposition: attachment, which aborts inline
          <video> playback in every browser — downloaded once at build time instead). Using the
          720p file only: Mixkit's 1080p master is 77MB, impractical for an autoplaying login
          background regardless of display density; 720p already reads as crisp behind the
          30% overlay + blur, at a tenth of the payload. `src` is a blob: URL (see the
          fetch-as-blob effect above) rather than a plain path, to work around this dev
          environment's blanket no-store header breaking <video>'s native range-request buffering. */}
      {videoSrc && (
        <video
          autoPlay muted loop playsInline preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          aria-hidden="true"
          src={videoSrc}
        />
      )}
      {/* Flat 30% black overlay for readability, per spec */}
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.3)" }} />
      {/* Extra gradient toward the card's side only, so the right-anchored card gets more contrast without darkening the whole frame */}
      <div className="absolute inset-0 hidden md:block" style={{ background: "linear-gradient(to right, transparent 45%, rgba(6,6,8,0.55) 100%)" }} />

      <motion.div
        initial={reducedMotion ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 10, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={reducedMotion ? { duration: 0 } : { duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-[400px] mx-6 md:mx-0 md:mr-[8vw] lg:mr-[10vw]"
      >
        <div
          className="rounded-[20px] p-9"
          style={{
            background: "rgba(18,18,22,0.58)",
            backdropFilter: "blur(28px)",
            WebkitBackdropFilter: "blur(28px)",
            border: "1px solid rgba(255,255,255,0.14)",
            boxShadow: "0 30px 70px rgba(0,0,0,0.5)",
          }}
        >
          <div className="flex items-center gap-3 mb-7">
            <div className="w-11 h-11 rounded-[11px] flex items-center justify-center text-white font-bold text-lg shrink-0" style={{ background: "#D6202B", boxShadow: "0 6px 18px rgba(214, 32, 43,0.4)" }}>FA</div>
            <div>
              <p className="font-bold text-xl leading-tight text-white" style={{ letterSpacing: "-0.02em" }}>FitFrikArena</p>
              <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: "rgba(255,255,255,0.55)" }}>Gym Operating System</p>
            </div>
          </div>

          {mode === "trial" && (
            <TrialSignupForm onBackToLogin={() => { setMode("login"); setError(""); }} />
          )}

          {mode === "login" && !mfaPending && !mustChangePassword && !showReset && (
            <form onSubmit={submitLogin} className="space-y-4" noValidate>
              <Field label="Email" testId={AUTH.emailInput} value={email} onChange={setEmail} type="email" error={fieldErrors.email} />
              <Field label="Password" testId={AUTH.passwordInput} value={password} onChange={setPassword} type="password" error={fieldErrors.password} />
              <SubmitButton testId={AUTH.loginSubmit} busy={busy} label="Log in" />
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  data-testid={AUTH.forgotPasswordLink}
                  onClick={() => { setShowReset(true); setFieldErrors({}); }}
                  className="text-sm font-medium transition-colors hover:opacity-80"
                  style={{ color: "#FF4436" }}
                >
                  Forgot password?
                </button>
              </div>
              <div className="pt-2 border-t" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                <button
                  type="button"
                  data-testid={TRIAL.startTrialLink}
                  onClick={() => setMode("trial")}
                  className="w-full flex items-center justify-center gap-2 mt-3 hover:opacity-90 transition-opacity"
                >
                  <span className="text-sm font-medium" style={{ color: "rgba(255,255,255,0.75)" }}>New gym?</span>
                  <motion.span
                    animate={reducedMotion ? {} : { boxShadow: ["0 0 0 0 rgba(214, 32, 43,0.45)", "0 0 0 6px rgba(214, 32, 43,0)"] }}
                    transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-[10.5px] font-extrabold uppercase tracking-wide"
                    style={{ background: "linear-gradient(135deg,#D6202B,#FF5A46)", color: "#ffffff" }}
                  >
                    Apply now
                  </motion.span>
                  <span className="text-sm font-semibold" style={{ color: "#FF4436" }}>Get started →</span>
                </button>
              </div>
            </form>
          )}

          {mode === "login" && mfaPending && (
            <form onSubmit={submitMfa} className="space-y-4" noValidate>
              <p className="text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>
                Enter the code we sent to <BlurredEmail email={email} />.
              </p>
              <Field label="MFA code" testId={AUTH.mfaCodeInput} value={mfaCode} onChange={setMfaCode} error={fieldErrors.mfaCode} />
              <SubmitButton testId={AUTH.mfaSubmit} busy={busy} label="Verify" />
            </form>
          )}

          {mode === "login" && mustChangePassword && (
            <form onSubmit={submitNewPassword} className="space-y-4" noValidate>
              <p className="text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>
                You're using a temporary password. Set a new one to continue — whenever you need any help, just click the help icon inside your dashboard to show the full journey again.
              </p>
              <Field label="New password" testId={AUTH.newPasswordInput} value={newPassword} onChange={setNewPassword} type="password" />
              <Field label="Confirm new password" testId={AUTH.confirmPasswordInput} value={confirmPassword} onChange={setConfirmPassword} type="password" />
              <SubmitButton testId={AUTH.forcePasswordSubmit} busy={busy} label="Set password & continue" />
            </form>
          )}

          {mode === "login" && showReset && resetDone && (
            <div className="space-y-4">
              <p data-testid={AUTH.resetMessage} className="text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>
                Your password has been reset. You can now log in with your new password.
              </p>
              <button
                type="button"
                data-testid={AUTH.resetBackToLogin}
                onClick={backToLogin}
                className="text-sm font-medium transition-colors hover:opacity-80"
                style={{ color: "#FF4436" }}
              >
                Back to login
              </button>
            </div>
          )}

          {mode === "login" && showReset && !resetDone && !resetRequested && (
            <form onSubmit={submitReset} className="space-y-4" noValidate>
              <Field label="Email to reset" testId={AUTH.resetEmailInput} value={resetEmail} onChange={setResetEmail} type="email" error={fieldErrors.resetEmail} />
              <SubmitButton testId={AUTH.resetSubmit} busy={busy} label="Request reset" />
              {resetMessage && <p data-testid={AUTH.resetMessage} className="text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>{resetMessage}</p>}
              <button
                type="button"
                data-testid={AUTH.resetBackToLogin}
                onClick={backToLogin}
                className="text-sm font-medium transition-colors hover:opacity-80"
                style={{ color: "rgba(255,255,255,0.75)" }}
              >
                Back to login
              </button>
            </form>
          )}

          {mode === "login" && showReset && !resetDone && resetRequested && (
            <form onSubmit={submitResetConfirm} className="space-y-4" noValidate>
              {resetMessage && <p data-testid={AUTH.resetMessage} className="text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>{resetMessage}</p>}
              <Field label="Reset token (from email)" testId={AUTH.resetTokenInput} value={resetToken} onChange={setResetToken} error={fieldErrors.resetToken} />
              <Field label="New password" testId={AUTH.resetNewPasswordInput} value={resetNewPassword} onChange={setResetNewPassword} type="password" error={fieldErrors.resetNewPassword} />
              <Field label="Confirm new password" testId={AUTH.resetConfirmPasswordInput} value={resetConfirmPassword} onChange={setResetConfirmPassword} type="password" error={fieldErrors.resetConfirmPassword} />
              <SubmitButton testId={AUTH.resetConfirmSubmit} busy={busy} label="Reset password" />
              <button
                type="button"
                data-testid={AUTH.resetBackToLogin}
                onClick={backToLogin}
                className="text-sm font-medium transition-colors hover:opacity-80"
                style={{ color: "rgba(255,255,255,0.75)" }}
              >
                Back to login
              </button>
            </form>
          )}

          {mode === "login" && error && (
            <motion.p {...fadeIn(reducedMotion)} data-testid={AUTH.loginError} className="text-sm font-medium mt-4" style={{ color: "#FF7C62" }}>
              {error}
            </motion.p>
          )}
        </div>

        <KairooAttribution tone="light" className="mt-5" />
      </motion.div>
    </div>
  );
}

function Field({ label, testId, value, onChange, type = "text", error }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: "rgba(255,255,255,0.55)" }}>
        {label} <span style={{ color: "#FF7C62" }}>*</span>
      </label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={!!error}
        className="w-full px-3.5 py-2.5 rounded-[10px] text-sm outline-none transition-shadow text-white placeholder:text-white/30"
        style={{
          border: error ? "1px solid #FF7C62" : "1px solid rgba(255,255,255,0.16)",
          background: "rgba(255,255,255,0.06)",
          boxShadow: error ? "0 0 0 3px rgba(255, 124, 98,0.15)" : "none",
        }}
        onFocus={(e) => { e.target.style.boxShadow = error ? "0 0 0 3px rgba(255, 124, 98,0.2)" : "0 0 0 3px rgba(214, 32, 43,0.25)"; e.target.style.borderColor = error ? "#FF7C62" : "#D6202B"; }}
        onBlur={(e) => { e.target.style.boxShadow = error ? "0 0 0 3px rgba(255, 124, 98,0.15)" : "none"; e.target.style.borderColor = error ? "#FF7C62" : "rgba(255,255,255,0.16)"; }}
      />
      {error && <p data-testid={`${testId}-error`} className="text-xs font-medium" style={{ color: "#FF7C62" }}>{error}</p>}
    </div>
  );
}

// Privacy micro-interaction for the MFA screen: shows the address the code went to (so the user
// can confirm it's the right inbox) without printing it in full — most of the local part is
// visually blurred rather than character-masked, a softer "peek" than asterisks.
function BlurredEmail({ email }) {
  const [local, domain] = (email || "").split("@");
  if (!local || !domain) return null;
  const visible = local.slice(0, Math.min(2, local.length));
  const hidden = local.slice(visible.length);
  return (
    <span className="font-semibold" style={{ color: "rgba(255,255,255,0.9)" }}>
      {visible}
      {hidden && (
        <span style={{ filter: "blur(3.5px)", WebkitFilter: "blur(3.5px)" }}>{hidden}</span>
      )}
      @{domain}
    </span>
  );
}

function SubmitButton({ testId, busy, label }) {
  return (
    <button
      type="submit"
      data-testid={testId}
      disabled={busy}
      className="w-full py-2.5 rounded-[12px] text-sm font-semibold text-white flex items-center justify-center gap-2 transition-transform disabled:opacity-70 hover:scale-[1.01] active:scale-[0.99]"
      style={{ background: "#D6202B", boxShadow: "0 8px 22px rgba(214, 32, 43,0.35)" }}
    >
      {busy ? <Loader2 size={16} className="animate-spin" /> : label}
    </button>
  );
}
